import axios from 'axios';
import { AuthClient } from '../auth/AuthClient';
export var ValidationJobPriority;
(function (ValidationJobPriority) {
    ValidationJobPriority[ValidationJobPriority["HIGHEST"] = 0] = "HIGHEST";
    ValidationJobPriority[ValidationJobPriority["HIGH"] = 1] = "HIGH";
    ValidationJobPriority[ValidationJobPriority["MEDIUM"] = 2] = "MEDIUM";
    ValidationJobPriority[ValidationJobPriority["LOW"] = 3] = "LOW";
})(ValidationJobPriority || (ValidationJobPriority = {}));
export function extractContentDispositionFilename(disposition) {
    const rfc5987 = /filename\*\s*=\s*([^']*)''([^;]+)/i.exec(disposition);
    if (rfc5987) {
        try {
            return decodeURIComponent(rfc5987[2].trim());
        }
        catch {
        }
    }
    const quoted = /filename\s*=\s*"((?:[^"\\]|\\.)*)"/i.exec(disposition);
    if (quoted)
        return quoted[1].replace(/\\(.)/g, '$1');
    const bare = /filename\s*=\s*([^;]+)/i.exec(disposition);
    if (bare)
        return bare[1].trim();
    return null;
}
export function handleError(error, code) {
    if (error.response) {
        return {
            code: code || error.response.data?.error || 'ERR_SERVER',
            message: error.response.data?.message || error.response.data?.error || error.message,
            status: error.response.status,
            details: error.response.data,
        };
    }
    else if (error.request) {
        return {
            code: code || 'ERR_NO_RESPONSE',
            message: 'No response from server',
            details: error,
        };
    }
    else {
        return {
            code: code || 'ERR_REQUEST_SETUP',
            message: error.message,
            details: error,
        };
    }
}
class ApiClientImpl {
    constructor(config) {
        this.axios = axios.create({
            baseURL: config.baseURL,
            timeout: config.timeout || 30000,
            headers: {
                'Content-Type': 'application/json',
            },
        });
        this.setupInterceptors();
    }
    setupInterceptors() {
        this.axios.interceptors.request.use((config) => {
            const token = AuthClient.getAccessToken();
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        }, (error) => {
            return Promise.reject(handleError(error));
        });
        this.axios.interceptors.response.use((response) => response, async (error) => {
            if (error.response?.status === 401) {
                console.warn('Unauthorized - redirecting to login');
                AuthClient.logout();
                return Promise.reject(handleError(error, 'ERR_UNAUTHORIZED'));
            }
            if (error.response?.status === 403) {
                return Promise.reject(handleError(error, 'ERR_FORBIDDEN'));
            }
            if (!error.response) {
                return Promise.reject(handleError(error, 'ERR_NETWORK'));
            }
            return Promise.reject(handleError(error));
        });
    }
    async get(url, config) {
        const response = await this.axios.get(url, config);
        return response.data.data;
    }
    async post(url, data, config) {
        const response = await this.axios.post(url, data, config);
        return response.data.data;
    }
    async put(url, data, config) {
        const response = await this.axios.put(url, data, config);
        return response.data.data;
    }
    async delete(url, config) {
        const response = await this.axios.delete(url, config);
        return response.data.data;
    }
    async patch(url, data, config) {
        const response = await this.axios.patch(url, data, config);
        return response.data.data;
    }
    updateBaseURL(url) {
        this.axios.defaults.baseURL = url;
    }
    getAxiosInstance() {
        return this.axios;
    }
}
export const apiClient = new ApiClientImpl({
    baseURL: `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/dev/v1'}`,
    timeout: 30000,
});
export const api = {
    health: {
        check: () => apiClient.get('/health'),
    },
    batches: {
        list: (params) => apiClient.get('/batches', { params }),
        get: (batchId) => apiClient.get(`/batches/${batchId}`),
        delete: (batchId) => apiClient.delete(`/batches/${batchId}`),
        deleteCollection: (batchId, collectionId) => apiClient.delete(`/batches/${batchId}/collections/${collectionId}`),
        close: (batchId) => apiClient.post(`/batches/${batchId}/close`, {}),
        updateStatus: (batchId, status) => apiClient.patch(`/batches/${batchId}/status`, { status }),
        updateDescription: (batchId, description) => apiClient.patch(`/batches/${batchId}/description`, { description }),
        stats: (batchId) => apiClient.get(`/batches/${batchId}/stats`),
        statsBulk: (batchIds) => apiClient.post('/batches/stats/bulk', { batchIds }),
        recalculateStats: (batchId) => apiClient.post(`/batches/${batchId}/stats/recalculate`),
        comments: {
            list: (batchId) => apiClient.get(`/batches/${batchId}/comments`),
            add: (batchId, text) => apiClient.post(`/batches/${batchId}/comments`, { text }),
            update: (batchId, commentId, text) => apiClient.patch(`/batches/${batchId}/comments/${commentId}`, { text }),
            remove: (batchId, commentId) => apiClient.delete(`/batches/${batchId}/comments/${commentId}`),
        },
        createFromHubList: (batchId, description) => apiClient.post('/batches/create-from-hub-list', {
            batchId,
            ...(description ? { description } : {}),
        }),
        upload: async (file, batchId, description) => {
            const content = await file.text();
            return apiClient.post('/batches/upload', {
                content,
                filename: file.name,
                batchId,
                ...(description ? { description } : {}),
            });
        },
        addCollections: async (batchId, file) => {
            const content = await file.text();
            return apiClient.post(`/batches/${batchId}/collections`, {
                content,
                filename: file.name,
            });
        },
    },
    collections: {
        list: (params) => apiClient.get('/collections', { params }),
        listFailed: (params) => apiClient.get('/collections/failed', { params }),
        get: (collectionId) => apiClient.get(`/collections/${collectionId}`),
        triage: (collectionId) => apiClient.get(`/collections/${collectionId}/triage`),
        retriggerIncrementalBulk: (collectionIds, batchId) => apiClient.post('/collections/retrigger-incremental', { collectionIds, batchId }),
        completeSnapshotEvents: (collectionId) => apiClient.post(`/collections/${collectionId}/complete-snapshot-events`),
        count: (params) => apiClient.get('/collections/count', { params }),
    },
    dashboard: {
        summary: () => apiClient.get('/dashboard/summary'),
    },
    settings: {
        get: () => apiClient.get('/settings'),
        patch: (payload) => apiClient.patch('/settings', payload),
        fixCeHubTime: () => apiClient.post('/settings/fix-ce-hub-time', {}),
        fix200HubTime: (content) => apiClient.post('/settings/fix-200-hub-time', { content }),
    },
    validations: {
        resume: () => apiClient.post('/validations/resume', {}),
        predefinedLineageUploadUrl: (body) => apiClient.post('/validations/predefined-lineage-upload-url', body),
    },
    metrics: {
        get: (startDate, endDate, granularity) => apiClient.get('/metrics', { params: { startDate, endDate, granularity } }),
    },
    changes: {
        list: (params) => apiClient.get('/changes', { params }),
    },
    jobs: {
        list: (params) => apiClient.get('/jobs', { params }),
        create: (data) => apiClient.post('/jobs', data),
        get: (jobId) => apiClient.get(`/jobs/${jobId}`),
        cancel: (jobId) => apiClient.post(`/jobs/${jobId}/cancel`, {}),
    },
    dataScan: {
        getCheckpoints: () => apiClient.get('/data-scan/checkpoints'),
    },
    dataFix: {
        getDbRefreshTime: () => apiClient.get('/data-fix/db-refresh-time'),
        getCases: () => apiClient.get('/data-fix/cases'),
        createJob: (payload) => apiClient.post('/data-fix/jobs', payload),
        cancelJob: (jobId, batchId) => apiClient.post(`/data-fix/jobs/${jobId}/cancel`, { batchId }),
        getJobs: (params) => apiClient.get('/data-fix/jobs', { params: params ?? {} }),
        getJobDownloadUrl: (params) => apiClient.get('/data-fix/jobs/download-url', { params }),
        downloadAll: (params) => apiClient.get('/data-fix/jobs/download-all', { params }),
        createDbRefreshRecord: (snapshotTime) => apiClient.post('/data-fix/db-refresh-history', { snapshotTime }),
        createFullAthenaQueryJob: (dbRefreshTime) => apiClient.post('/data-fix/full-athena-query', { dbRefreshTime }),
        downloadFailedRecordReport: async (dbRefreshDate) => {
            const response = await apiClient
                .getAxiosInstance()
                .get(`/data-fix/failed-report/${dbRefreshDate}`, { responseType: 'blob' });
            const disposition = (response.headers['content-disposition'] ?? '');
            const filename = extractContentDispositionFilename(disposition) ?? `failed-records-${dbRefreshDate}.csv`;
            return { blob: response.data, filename };
        },
        getDbRefreshHistory: (nextToken) => apiClient.get('/data-fix/db-refresh-history', nextToken ? { params: { nextToken } } : undefined),
        getDataFixCollectionRecords: (params) => apiClient.get('/data-fix/data-fix-collection-records', { params }),
        downloadDataFixCollectionRecords: async (params) => {
            const response = await apiClient
                .getAxiosInstance()
                .get('/data-fix/data-fix-collection-records/download', {
                params,
                responseType: 'blob',
            });
            const disposition = (response.headers['content-disposition'] ?? '');
            const filename = extractContentDispositionFilename(disposition) ??
                `collection-records-${params.dbRefreshDate}-${params.batchId}-${params.caseName}.csv`;
            return { blob: response.data, filename };
        },
    },
};
